import { ApplicationTypeKey } from "@hub/data/values/data-keys.enum";
import { BannerConfig } from "@hub/models/ui/banner-config";
import { Breadcrumb } from "@hub/models/ui/breadcrumb";
import { DocumentCardData } from "@hub/models/document/document-card-data";
import { CodeSampleFilterGroups } from "@hub/models/filters/code-sample-filter-groups";
import { HeadMetadata } from "@hub/models/metadata/head-metadata";
import { ContentImage } from "@hub/models/ui/content-image";
import { NavigationFolder } from "@hub/models/ui/navigation-folder";
import { NavigationFolders } from "@hub/models/ui/navigation-folders";
import { PageConfig } from "@hub/models/ui/page-config";
import { hubOriginUrl, hubSiteTree } from "@hub/services/config.service";
import { getBreadCrumbs } from "@hub/services/content/common/get-breadcrumbs";
import { CodeSampleData } from "@hub/services/content/models/code-sample-data";
import { ContentFilteringService } from "@hub/services/filtering/content-filtering.service";
import { MetadataService } from "@hub/services/metadata.service";
import { createUrlPath } from "@hub/services/utils/path.utils";
import { AllCodeSamplesPage } from "@hub/ui/pages/all-code-samples-page";
import startCase from "lodash/startCase";
import { GetStaticProps } from "next";
import { useRouter } from "next/router";
import React, { useEffect } from "react";

interface CodeSamplesIndexProps {
  canonicalUrl: string;
  contentCardData: DocumentCardData[];
  filterOptions: CodeSampleFilterGroups;
  navigationFolders?: NavigationFolders;
  breadcrumbs?: Breadcrumb[];
}

const CodeSamplesIndex: React.FC<CodeSamplesIndexProps> = ({
  canonicalUrl,
  contentCardData,
  filterOptions,
  navigationFolders,
  breadcrumbs,
}) => {
  const router = useRouter();
  const { query, isReady } = router;

  const filterQuery = query as {
    type: string;
    framework: string;
    language: string;
    searchTerm: string;
  };

  useEffect(() => {
    if (isReady && contentCardData) {
      const enabledFilters: {
        topic: boolean;
        type: boolean;
        framework: boolean;
        language: boolean;
      } = {
        topic: false,
        type: true,
        framework: true,
        language: true,
      };

      ContentFilteringService.setEnabledFilterGroups(enabledFilters);
      ContentFilteringService.setFilterOptions(filterOptions);
      ContentFilteringService.initializeFilterGroups({
        query: filterQuery,
        contentCardData,
      });
    }
  }, [filterOptions, filterQuery, isReady, contentCardData]);

  const headMetadata: HeadMetadata = {
    title: "Auth0 Code Samples",
    description:
      "Explore how to implement authentication and authorization using different frameworks and languages.",
    url: canonicalUrl,
    image:
      "https://cdn.auth0.com/website/developer-center/preview-cards/resources_code-samples.png",
    type: "website",
  };

  const image: ContentImage = {
    src: "https://cdn.auth0.com/blog/developer-hub/code-samples-logo.svg",
    alt: "You can implement Auth0 to protect multiple applications that run on multiple devices",
  };

  const compactImage: ContentImage = {
    src: "https://cdn.auth0.com/blog/developer-hub/code-samples-logo.svg",
    alt: "You can implement Auth0 to protect multiple applications that run on multiple devices",
  };

  const pageConfig: PageConfig = {
    canonicalUrl,
    title: "Auth0 Authentication and Authorization Code Samples",
    description:
      "Use different frontend and backend frameworks and languages to explore the authentication and authorization features of the Auth0 Identity Platform.",
  };

  const bannerConfig: BannerConfig = {
    title: "Code Samples",
    description:
      "Experience the identity and security features of Auth0",
    illustration: {
      desktop: image,
      mobile: compactImage,
    },
    withBorder: true,
  };

  return (
    <AllCodeSamplesPage
      bannerConfig={bannerConfig}
      pageConfig={pageConfig}
      navigationFolders={navigationFolders}
      breadcrumbs={breadcrumbs}
      headMetadata={headMetadata}
    />
  );
};

export default CodeSamplesIndex;

export const getStaticProps: GetStaticProps<
  CodeSamplesIndexProps
> = async () => {
  const codeSampleData = new CodeSampleData();

  const navigationFolders: NavigationFolder[] =
    codeSampleData.allowed.applicationTypes
      .map((type) => type as ApplicationTypeKey)
      .map((applicationType) => {
        const folder: {
          label: string;
          path: string;
        } = {
          label:
            MetadataService.getApplicationTypeDisplayName(applicationType) ||
            startCase(applicationType),
          path: createUrlPath([
            hubSiteTree.root.urlPath,
            hubSiteTree.root.sections.codeSamples.urlPath,
            applicationType,
          ]),
        };

        return folder;
      });

  const breadcrumbs = getBreadCrumbs({
    root: {
      label: hubSiteTree.root.displayName,
      path: hubSiteTree.root.urlPath,
    },
    sections: [
      {
        label: hubSiteTree.root.sections.codeSamples.displayName,
        path: hubSiteTree.root.sections.codeSamples.urlPath,
      },
    ],
  });

  const canonicalUrl =
    hubOriginUrl +
    createUrlPath([
      hubSiteTree.root.urlPath,
      hubSiteTree.root.sections.codeSamples.urlPath,
    ]);

  const props: CodeSamplesIndexProps = {
    canonicalUrl,
    contentCardData: codeSampleData.cards.codeSamples,
    filterOptions: codeSampleData.filters,
    navigationFolders: {
      group: "Application Type",
      folders: navigationFolders,
      activePath: createUrlPath([
        hubSiteTree.root.urlPath,
        hubSiteTree.root.sections.codeSamples.urlPath,
      ]),
    },
    breadcrumbs,
  };

  return {
    props,
  };
};
